import { useState, useCallback, useEffect, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Server,
  Database,
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Loader2,
  Inbox,
  Wifi,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import { usePaginatedData, type PaginatedResponse } from "@/hooks/usePaginatedData";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  platformApi,
  type ContainerAppHealth,
  type PlatformHealthDigest,
} from "@/services/api/platform";
import { WorkflowRunHealthCard } from "@/components/portal/workflows/WorkflowRunHealthCard";
import { ScheduleRunHealthCard } from "@/components/portal/schedules/ScheduleRunHealthCard";
import {
  fetchWorkflowRunHealth,
  fetchScheduleRunHealth,
  type WorkflowRunHealth,
  type ScheduleRunHealth,
} from "@/lib/portalData";

export const Route = createFileRoute("/portal/platform/monitoring")({
  component: MonitoringPage,
});

function appIssueReason(app: ContainerAppHealth): string {
  const reasons: string[] = [];
  if (app.cpu_percent !== null && app.cpu_percent > 80) reasons.push(`CPU ${app.cpu_percent}%`);
  if (app.memory_percent !== null && app.memory_percent > 85) reasons.push(`Mem ${app.memory_percent}%`);
  if (app.restart_count !== null && app.restart_count > 0) reasons.push(`${app.restart_count} restart${app.restart_count > 1 ? "s" : ""}`);
  return reasons.length > 0 ? reasons.join(", ") : app.status;
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "healthy":
      return (
        <Badge variant="success">
          <CheckCircle className="mr-1 h-3 w-3" />
          Healthy
        </Badge>
      );
    case "warning":
    case "degraded":
      return (
        <Badge variant="warning">
          <AlertTriangle className="mr-1 h-3 w-3" />
          {status === "degraded" ? "Degraded" : "Warning"}
        </Badge>
      );
    case "critical":
    case "down":
      return (
        <Badge variant="destructive">
          <XCircle className="mr-1 h-3 w-3" />
          {status === "critical" ? "Critical" : "Down"}
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function MonitoringPage() {
  const { token } = useAuth();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [digest, setDigest] = useState<PlatformHealthDigest | null>(null);
  const [digestLoading, setDigestLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Load overview digest (for summary cards + tabs)
  const loadDigest = useCallback(async () => {
    if (!token) return;
    try {
      const result = await platformApi.getPlatformHealth(token);
      setDigest(result);
    } catch {
      // 503 = no cached data yet, that's OK
    } finally {
      setDigestLoading(false);
    }
  }, [token]);

  // Load digest on mount
  useEffect(() => {
    loadDigest();
  }, [loadDigest]);

  // v2 workflow run-outcome health. Fetched separately from the digest so the panel
  // reflects live state on demand rather than whatever the 4-hourly collection cached
  // — a run-health panel showing hours-old data is how a broken workflow goes unnoticed.
  const [workflowRuns, setWorkflowRuns] = useState<WorkflowRunHealth | null>(null);
  const [workflowRunsLoading, setWorkflowRunsLoading] = useState(true);
  const [workflowRunsStale, setWorkflowRunsStale] = useState(false);
  // Monotonic request id. The 60s interval and the manual refresh can overlap, and
  // without this an older response can resolve last and overwrite a newer one for a
  // full minute.
  const workflowRunsSeq = useRef(0);

  const loadWorkflowRuns = useCallback(async () => {
    if (!token) return;
    const seq = ++workflowRunsSeq.current;
    setWorkflowRunsLoading(true);
    try {
      const result = await fetchWorkflowRunHealth(token);
      if (seq !== workflowRunsSeq.current) return; // superseded
      setWorkflowRuns(result);
      setWorkflowRunsStale(false);
    } catch {
      if (seq !== workflowRunsSeq.current) return;
      // Keep the last good response and mark it stale rather than nulling it. One
      // transient blip must not erase a live incident from the panel — and a failed
      // fetch is not evidence the database is down.
      setWorkflowRunsStale(true);
    } finally {
      if (seq === workflowRunsSeq.current) setWorkflowRunsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadWorkflowRuns();
    const timer = setInterval(loadWorkflowRuns, 60_000);
    return () => clearInterval(timer);
  }, [loadWorkflowRuns]);

  // CP scheduler run health. Same independent-fetch + staleness pattern as the
  // workflow panel: the scheduler is upstream of most other signals, so a panel
  // showing hours-old data is how a dead ticker stays unnoticed.
  const [scheduleRuns, setScheduleRuns] = useState<ScheduleRunHealth | null>(null);
  const [scheduleRunsLoading, setScheduleRunsLoading] = useState(true);
  const [scheduleRunsStale, setScheduleRunsStale] = useState(false);
  const scheduleRunsSeq = useRef(0);

  const loadScheduleRuns = useCallback(async () => {
    if (!token) return;
    const seq = ++scheduleRunsSeq.current;
    setScheduleRunsLoading(true);
    try {
      const result = await fetchScheduleRunHealth(token);
      if (seq !== scheduleRunsSeq.current) return; // superseded
      setScheduleRuns(result);
      setScheduleRunsStale(false);
    } catch {
      if (seq !== scheduleRunsSeq.current) return;
      // Keep the last good response — one transient blip must not erase a live
      // incident, and a failed fetch is not evidence the database is down.
      setScheduleRunsStale(true);
    } finally {
      if (seq === scheduleRunsSeq.current) setScheduleRunsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadScheduleRuns();
    const timer = setInterval(loadScheduleRuns, 60_000);
    return () => clearInterval(timer);
  }, [loadScheduleRuns]);

  // Server-side paginated container apps
  const fetchContainerApps = useCallback(
    async (page: number, pageSize: number): Promise<PaginatedResponse<ContainerAppHealth>> => {
      if (!token) return { data: [], totalCount: 0, page: 1, pageSize: 25 };
      const offset = (page - 1) * pageSize;
      const result = await platformApi.getContainerApps(token, {
        offset,
        limit: pageSize,
        search: search || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      return {
        data: result.items,
        totalCount: result.total,
        page,
        pageSize,
      };
    },
    [token, search, sortBy, sortOrder],
  );

  const {
    data: containerApps,
    isLoading: appsLoading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh: refreshApps,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<ContainerAppHealth>({
    fetchFn: fetchContainerApps,
    initialPageSize: 25,
    dependencies: [search, sortBy, sortOrder],
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }

  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  async function handleRefresh() {
    if (!token) return;
    setRefreshing(true);
    try {
      const freshDigest = await platformApi.refreshPlatformHealth(token);
      setDigest(freshDigest);
      refreshApps();
      toast.success("Platform health refreshed");
    } catch {
      toast.error("Failed to refresh platform health");
    } finally {
      setRefreshing(false);
    }
  }

  const overallColor =
    digest?.overall_status === "healthy"
      ? "text-green-600"
      : digest?.overall_status === "degraded"
        ? "text-yellow-600"
        : digest?.overall_status === "critical"
          ? "text-red-600"
          : "text-muted-foreground";

  const overallLabel =
    digest?.overall_status === "healthy"
      ? "All Systems Operational"
      : digest?.overall_status === "degraded"
        ? "Degraded"
        : digest?.overall_status === "critical"
          ? "Critical Issues"
          : "Unknown";

  const unhealthyApps = digest?.container_apps.filter(
    (a) => a.status === "warning" || a.status === "critical",
  ) ?? [];
  const unhealthyCount = unhealthyApps.length;

  if (digestLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Platform Monitoring</h1>
          <p className="mt-2 text-lg text-slate-600">
            Real-time infrastructure health
            {digest?.timestamp && (
              <> &middot; {new Date(digest.timestamp).toLocaleTimeString()}</>
            )}
          </p>
        </div>
        <Button
          className="rounded-full"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overall Status</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${overallColor}`}>
              {overallLabel}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Container Apps</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {digest?.container_apps.length ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {unhealthyCount === 0
                ? "All reporting normally"
                : unhealthyCount <= 3
                  ? unhealthyApps.map((a) => `${a.name} (${appIssueReason(a)})`).join(", ")
                  : `${unhealthyCount} need attention`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Queue Depth</CardTitle>
            <Inbox className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {digest?.redis?.total_pending_jobs ?? "--"}
            </div>
            <p className="text-xs text-muted-foreground">
              {digest?.redis?.connected ? "Pending jobs across all queues" : "Redis unavailable"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Databases</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {digest?.mssql?.connected && digest?.postgres?.connected
                ? "Online"
                : digest?.mssql?.connected || digest?.postgres?.connected
                  ? "Partial"
                  : "--"}
            </div>
            <p className="text-xs text-muted-foreground">
              MSSQL {digest?.mssql?.active_connections ?? "--"} conn
              {digest?.postgres ? ` | PG ${digest.postgres.active_connections ?? "--"} conn` : ""}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Container Apps Data Grid */}
      <div className="rounded-3xl border border-slate-100 bg-white shadow-sm">
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Container Apps</h2>
              <p className="text-sm text-muted-foreground">
                CPU, memory, replicas, and restart counts from Azure Monitor
              </p>
            </div>
            <SearchInput
              placeholder="Search apps..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch("")}
              containerClassName="w-64"
            />
          </div>
        </div>

        <div className="px-6">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  direction={getSortDirection("name")}
                  onSort={() => handleSort("name")}
                >
                  Name
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("cpu_percent")}
                  onSort={() => handleSort("cpu_percent")}
                  className="w-[140px]"
                >
                  CPU %
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("memory_percent")}
                  onSort={() => handleSort("memory_percent")}
                  className="w-[140px]"
                >
                  Memory %
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("replicas")}
                  onSort={() => handleSort("replicas")}
                  className="w-[100px]"
                >
                  Replicas
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("restart_count")}
                  onSort={() => handleSort("restart_count")}
                  className="w-[100px]"
                >
                  Restarts
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("status")}
                  onSort={() => handleSort("status")}
                  className="w-[120px]"
                >
                  Status
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appsLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : containerApps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    <Server className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    {search ? "No apps match your search" : "No container app data available"}
                  </TableCell>
                </TableRow>
              ) : (
                containerApps.map((app) => (
                  <TableRow key={app.name}>
                    <TableCell className="font-medium">{app.name}</TableCell>
                    <TableCell>
                      {app.cpu_percent !== null ? (
                        <div className="flex items-center gap-2">
                          <Progress value={app.cpu_percent} className="h-2 w-16" />
                          <span className="text-sm tabular-nums">{app.cpu_percent}%</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">--</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {app.memory_percent !== null ? (
                        <div className="flex items-center gap-2">
                          <Progress value={app.memory_percent} className="h-2 w-16" />
                          <span className="text-sm tabular-nums">{app.memory_percent}%</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">--</span>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {app.replicas ?? <span className="text-muted-foreground">--</span>}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {app.restart_count !== null ? (
                        <span className={app.restart_count > 0 ? "text-red-600 font-medium" : ""}>
                          {app.restart_count}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">--</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={app.status} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="p-6 pt-4">
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.pageSize}
            pageSizeOptions={pageSizeOptions}
            startIndex={startIndex}
            endIndex={endIndex}
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            onPageChange={goToPage}
            onPageSizeChange={setPageSize}
            onFirstPage={firstPage}
            onLastPage={lastPage}
            onPrevPage={prevPage}
            onNextPage={nextPage}
          />
        </div>
      </div>

      {/* Scheduler health — upstream of the workflow and connector signals: when the
          ticker dies they all go quiet at once and none of them names the cause. */}
      <ScheduleRunHealthCard
        runHealth={scheduleRuns}
        loading={scheduleRunsLoading}
        stale={scheduleRunsStale}
        onRefresh={loadScheduleRuns}
      />

      {/* Workflow run-outcome health — the signal the stream/queue panels above
          cannot give you. A workflow whose every run fails still drains its stream
          cleanly and still fires on schedule, so it reads healthy everywhere else. */}
      <WorkflowRunHealthCard
        runHealth={workflowRuns}
        loading={workflowRunsLoading}
        stale={workflowRunsStale}
        onRefresh={loadWorkflowRuns}
      />

      {/* Infrastructure Tabs */}
      <Tabs defaultValue="redis">
        <TabsList>
          <TabsTrigger value="redis">Redis</TabsTrigger>
          <TabsTrigger value="databases">Databases</TabsTrigger>
          <TabsTrigger value="services">Services</TabsTrigger>
        </TabsList>

        {/* Redis Tab */}
        <TabsContent value="redis" className="mt-6">
          <div className="grid gap-4 md:grid-cols-3 mb-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Status</CardTitle>
              </CardHeader>
              <CardContent>
                <StatusBadge status={digest?.redis?.connected ? "healthy" : "down"} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Memory</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {digest?.redis?.used_memory_human ?? "--"}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Clients</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {digest?.redis?.connected_clients ?? "--"}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">Queue Depths</CardTitle>
              <CardDescription className="text-xs">
                Pending jobs in ARQ worker queues
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {!digest?.redis?.queue_depths ||
              Object.keys(digest.redis.queue_depths).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Inbox className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No queue data available</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(digest.redis.queue_depths).map(
                    ([queue, depth]) => (
                      <div
                        key={queue}
                        className="flex items-center justify-between py-2 border-b last:border-0"
                      >
                        <span className="text-sm font-mono">{queue}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold">{depth}</span>
                          <Badge
                            variant={
                              depth === 0
                                ? "outline"
                                : depth > 100
                                  ? "destructive"
                                  : "warning"
                            }
                          >
                            {depth === 0 ? "Empty" : depth > 100 ? "High" : "Active"}
                          </Badge>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Databases Tab */}
        <TabsContent value="databases" className="mt-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="py-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">MSSQL</CardTitle>
                  <StatusBadge
                    status={digest?.mssql?.connected ? "healthy" : "down"}
                  />
                </div>
                <CardDescription className="text-xs">
                  Control Plane + Data Plane databases
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Active Connections</span>
                    <span className="text-sm font-bold">
                      {digest?.mssql?.active_connections ?? "--"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Blocked Requests</span>
                    <span
                      className={`text-sm font-bold ${
                        (digest?.mssql?.blocked_requests ?? 0) > 0
                          ? "text-red-600"
                          : ""
                      }`}
                    >
                      {digest?.mssql?.blocked_requests ?? "--"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Latency</span>
                    <span className="text-sm font-bold">
                      {digest?.mssql?.latency_ms != null
                        ? `${digest.mssql.latency_ms}ms`
                        : "--"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="py-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">PostgreSQL (RAG)</CardTitle>
                  <StatusBadge
                    status={
                      digest?.postgres == null
                        ? "unknown"
                        : digest.postgres.connected
                          ? "healthy"
                          : "down"
                    }
                  />
                </div>
                <CardDescription className="text-xs">
                  RAG database health metrics
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {digest?.postgres == null ? (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    Not configured (set POSTGRES_MONITOR_URL)
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Connections</span>
                      <span className="text-sm font-bold">
                        {digest.postgres.active_connections ?? "--"} / {digest.postgres.max_connections ?? "--"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Cache Hit Ratio</span>
                      <span
                        className={`text-sm font-bold ${
                          digest.postgres.cache_hit_ratio != null &&
                          digest.postgres.cache_hit_ratio < 95
                            ? "text-yellow-600"
                            : ""
                        }`}
                      >
                        {digest.postgres.cache_hit_ratio != null
                          ? `${digest.postgres.cache_hit_ratio}%`
                          : "--"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Dead Tuples</span>
                      <span className="text-sm font-bold">
                        {digest.postgres.dead_tuples?.toLocaleString() ?? "--"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Long Queries (&gt;30s)</span>
                      <span
                        className={`text-sm font-bold ${
                          (digest.postgres.long_running_queries ?? 0) > 0
                            ? "text-red-600"
                            : ""
                        }`}
                      >
                        {digest.postgres.long_running_queries ?? "--"}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="mt-6">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">Service Registry</CardTitle>
              <CardDescription className="text-xs">
                Data plane service health from the CP service registry
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {!digest?.services.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Wifi className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No registered services</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {digest.services.map((svc) => (
                    <div
                      key={svc.name}
                      className="flex items-center justify-between py-2 border-b last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        <Wifi className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{svc.name}</span>
                      </div>
                      <StatusBadge status={svc.healthy ? "healthy" : "down"} />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
